import React, { useEffect } from 'react';

import { intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes, { getCountryCode, getCountryLabel } from '../../translations/countryCodes';
import { getStateCode, STATE_OPTIONS } from '../../util/stateNames';
import { FieldTextInput, FieldSelect } from '../../components';

import css from './StripePaymentAddress.module.css';

/**
 * A component that renders a Stripe payment address.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {intlShape} props.intl - The intl object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {Object} props.form - The form object
 * @param {string} props.fieldId - The field id
 * @param {Object} props.card - The card object
 * @param {string} props.locale - The locale
 * @returns {JSX.Element}
 */
const StripePaymentAddress = props => {
  const { className, intl, disabled, form, fieldId, card, locale } = props;
  const formValues = form?.getState?.().values || {};
  const selectedCountry = formValues.country || '';
  const selectedState = formValues.state || '';
  const selectedCountryCode = getCountryCode(selectedCountry);
  const useUSStateSelect = selectedCountry === '' || selectedCountryCode === 'US';

  const optionalText = intl.formatMessage({
    id: 'StripePaymentAddress.optionalText',
  });

  const addressLine1Label = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Label',
  });
  const addressLine1Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Placeholder',
  });
  const addressLine1Required = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.addressLine1Required',
    })
  );

  const addressLine2Label = intl.formatMessage(
    { id: 'StripePaymentAddress.addressLine2Label' },
    { optionalText: optionalText }
  );

  const addressLine2Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine2Placeholder',
  });

  const postalCodeLabel = intl.formatMessage({ id: 'StripePaymentAddress.postalCodeLabel' });
  const postalCodePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.postalCodePlaceholder',
  });
  const postalCodeRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.postalCodeRequired',
    })
  );

  const cityLabel = intl.formatMessage({ id: 'StripePaymentAddress.cityLabel' });
  const cityPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.cityPlaceholder' });
  const cityRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.cityRequired',
    })
  );

  const stateLabel = intl.formatMessage(
    { id: 'StripePaymentAddress.stateLabel' },
    { optionalText: optionalText }
  );
  const statePlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.statePlaceholder' });
  const stateSelectPlaceholder = useUSStateSelect ? 'Select a state' : statePlaceholder;

  const countryLabel = intl.formatMessage({ id: 'StripePaymentAddress.countryLabel' });
  const countryPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.countryPlaceholder' });
  const countryRequiredMessage = intl.formatMessage({
    id: 'StripePaymentAddress.countryRequired',
  });
  const countryRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.countryRequired',
    })
  );
  const countrySupported = value =>
    !value || getCountryCode(value) ? undefined : countryRequiredMessage;

  const handleOnChange = event => {
    const value = event.target.value;
    form.change('postal', value);
    card.update({ value: { postalCode: value } });
  };

  // Use the language set in config.localization.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(locale);
  const prioritizedCountryCodes = [
    ...countryCodes.filter(country => country.code === 'US'),
    ...countryCodes.filter(country => country.code !== 'US'),
  ];

  const normalizeCountryLabel = value => {
    const normalizedLabel = getCountryLabel(value, locale);
    return normalizedLabel || '';
  };

  useEffect(() => {
    if (!useUSStateSelect || !selectedState) {
      return;
    }

    const normalizedStateCode = getStateCode(selectedState);
    if (normalizedStateCode && normalizedStateCode !== selectedState) {
      form.change('state', normalizedStateCode);
    }
  }, [form, selectedState, useUSStateSelect]);

  useEffect(() => {
    if (!selectedCountry) {
      form.change('country', getCountryLabel('US', locale));
      return;
    }

    const normalizedCountryLabel = normalizeCountryLabel(selectedCountry);
    if (normalizedCountryLabel && normalizedCountryLabel !== selectedCountry) {
      form.change('country', normalizedCountryLabel);
    }
  }, [form, locale, selectedCountry]);

  return (
    <div className={className ? className : css.root}>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}-addressLine1`}
          name="addressLine1"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-line1"
          label={addressLine1Label}
          placeholder={addressLine1Placeholder}
          validate={addressLine1Required}
          onUnmount={() => form.change('addressLine1', undefined)}
        />

        <FieldTextInput
          id={`${fieldId}-addressLine2`}
          name="addressLine2"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-line2"
          label={addressLine2Label}
          placeholder={addressLine2Placeholder}
          onUnmount={() => form.change('addressLine2', undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}-postalCode`}
          name="postal"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing postal-code"
          label={postalCodeLabel}
          placeholder={postalCodePlaceholder}
          validate={postalCodeRequired}
          onUnmount={() => form.change('postal', undefined)}
          onChange={event => handleOnChange(event)}
        />

        <FieldTextInput
          id={`${fieldId}-city`}
          name="city"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level2"
          label={cityLabel}
          placeholder={cityPlaceholder}
          validate={cityRequired}
          onUnmount={() => form.change('city', undefined)}
        />
      </div>
      <div className={css.formRow}>
        {useUSStateSelect ? (
          <FieldSelect
            id={`${fieldId}-state`}
            name="state"
            disabled={disabled}
            className={css.field}
            autoComplete="billing address-level1"
            label={stateLabel}
          >
            <option value="">{stateSelectPlaceholder}</option>
            {STATE_OPTIONS.map(state => (
              <option key={state.code} value={state.code}>
                {state.label}
              </option>
            ))}
          </FieldSelect>
        ) : (
          <FieldTextInput
            id={`${fieldId}-state`}
            name="state"
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="billing address-level1"
            label={stateLabel}
            placeholder={statePlaceholder}
            onUnmount={() => form.change('state', undefined)}
          />
        )}

        <FieldTextInput
          id={`${fieldId}-country`}
          name="country"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="country-name"
          label={countryLabel}
          placeholder={countryPlaceholder}
          list={`${fieldId}-countryOptions`}
          validate={validators.composeValidators(countryRequired, countrySupported)}
          onUnmount={() => form.change('country', undefined)}
        />
        <datalist id={`${fieldId}-countryOptions`}>
          {prioritizedCountryCodes.map(country => (
            <option key={country.code} value={`${country.code} - ${country.name}`} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

export default StripePaymentAddress;
